import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckSquare, Folder, House, MessageSquare, Plus } from 'lucide-react';
import { useMyBotsQuery } from '../../hooks/queries';
import { keptPath, lastShown, showTabSoon } from '../../layouts/TabScreens';
import { unreadChats } from '../../lib/sidebarChats';
import { useShellActions } from '../layout/shellActions';
import ChatUnreadDot from '../unread/ChatUnreadDot';
import { isChatScreen } from './mobileTitle';

// The four places, as in the desktop sidebar, with "New" in the middle. Tasks covers the
// Planner (its calendar); Chats the chats and the bots. "More" lives behind the avatar in
// MobileHeader.
const tabs = (t) => [
  { id: 'home', to: '/app/agenda', label: t('nav.home'), icon: House,
    match: (p) => p === '/app' || p === '/app/agenda' },
  { id: 'tasks', to: '/app/tasks', label: t('nav.tasks'), icon: CheckSquare,
    match: (p) => p === '/app/tasks' || p === '/app/planner' },
  { id: 'new' },
  { id: 'chats', to: '/app/chats', label: t('nav.chats'), icon: MessageSquare,
    match: (p) => p === '/app/chats' || p.startsWith('/app/chats/') || p === '/app/bots' || p.startsWith('/app/bots/') },
  { id: 'projects', to: '/app/projects', label: t('nav.projects'), icon: Folder,
    match: (p) => p === '/app/projects' || p.startsWith('/app/projects/') },
];

export default function MobileBottomNav() {
  const { t } = useTranslation();
  const location = useLocation();
  const { openNew } = useShellActions();
  const items = tabs(t);
  const { data: botsData } = useMyBotsQuery();
  const waiting = (botsData?.bots || []).reduce((sum, b) => sum + (b.pending_approvals || 0), 0);
  const unread = unreadChats(botsData);

  // The router draws the next page in a transition and keeps the old one until it is
  // ready, so the tapped tab lights up at once on its own; the page catches up.
  const [pressed, setPressed] = useState(null);
  // An open chat is a full-screen conversation: no tab bar, its top bar leads back.
  if (isChatScreen(location.pathname)) return null;
  const isActive = (tab) => (pressed?.from === location.key ? pressed.id === tab.id : tab.match(location.pathname));
  // Back to where the person left the tab: Tasks to its list or its calendar, Chats to the
  // page it showed (the screens stay drawn, layouts/TabScreens.jsx).
  const target = (tab) => {
    if (tab.id === 'tasks') {
      const last = lastShown(['tasks', 'planner']);
      return last === 'planner' ? keptPath('planner') || '/app/planner' : tab.to;
    }
    if (tab.id === 'chats') {
      const kept = keptPath('chats');
      return kept && !isChatScreen(kept.split('?')[0]) ? kept : tab.to;
    }
    return tab.to;
  };

  return (
    <nav
      className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-background/90 backdrop-blur-xl border-t border-border/50"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      role="navigation"
      aria-label={t('ui_shell.mobile.primary_nav')}
    >
      <ul className="flex items-stretch justify-around h-16">
        {items.map((tab) => {
          if (tab.id === 'new') {
            return (
              <li key="new" className="flex-1 min-w-0 flex items-center justify-center">
                <button type="button" onClick={() => openNew()} aria-label={t('nav.create.new')} data-testid="mobile-nav-new"
                  className="w-12 h-12 rounded-full bg-amber-500 text-honey-ink flex items-center justify-center shadow-lg shadow-amber-500/25 active:bg-amber-600 transition-colors">
                  <Plus className="w-6 h-6" strokeWidth={2.25} />
                </button>
              </li>
            );
          }
          const Icon = tab.icon;
          const active = isActive(tab);
          const to = target(tab);
          return (
            <li key={tab.id} className="flex-1 min-w-0">
              <NavLink
                to={to}
                onClick={() => {
                  setPressed({ id: tab.id, from: location.key });
                  showTabSoon(to);
                }}
                className={`flex flex-col items-center justify-center gap-1 h-full px-1 transition-colors ${
                  active ? 'text-amber-500' : 'text-muted-foreground hover:text-foreground'
                }`}
                aria-current={active ? 'page' : undefined}
                data-testid={`mobile-nav-${tab.id}`}
              >
                {/* Every tab has the same pill behind its icon; only the open tab fills it. */}
                <span className={`relative flex items-center justify-center w-14 h-8 rounded-full transition-colors ${
                  active ? 'bg-honey-soft text-amber-500' : ''
                }`}>
                  <Icon className="w-[22px] h-[22px]" strokeWidth={active ? 2.25 : 1.75} />
                  {/* Chats: what waits for the person (orange, counted), else a dot for unread. */}
                  {tab.id === 'chats' && waiting > 0 && (
                    <span className="absolute -top-0.5 right-1 min-w-[16px] h-4 px-1 rounded-full bg-attention text-honey-ink text-[10px] font-bold leading-4 text-center ring-2 ring-card">
                      {waiting}
                    </span>
                  )}
                  {tab.id === 'chats' && !(waiting > 0) && unread > 0 && <ChatUnreadDot className="absolute top-0 right-2.5 w-2 h-2 ring-2 ring-card" />}
                </span>
                <span className={`text-[11px] leading-none ${active ? 'font-semibold text-foreground' : 'font-medium'} truncate max-w-full`}>
                  {tab.label}
                </span>
              </NavLink>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
